import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/profile.css';
import api from '../../services/api';
import { useLanguage } from '../../utils/LanguageContext';
import EditProfileModal from '../../components/EditProfileModal';
import { getLocalized } from '../../utils/languageUtils';
import { ROUTES } from '../../routes/path';

const Profile = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  const [isEditing, setIsEditing] = useState(false);
  const [User, setUser] = useState(null);
  const [plantedTrees, setPlantedTrees] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/get/me/'),
      api.get('/get/my/planted/trees/'),
      api.get('/get/my/activities/')
    ])
      .then(([userRes, plantedRes, actRes]) => {
        setUser(userRes.data);
        setPlantedTrees(plantedRes.data || []);
        setActivities(actRes.data || []);
      })
      .catch(err => {
        console.error('Error fetching profile data:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  if (loading) return <div className="profile-container">Loading...</div>;
  if (!User) return <div className="profile-container">No profile data</div>;

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSaveProfile = (formData) => {
    return api.put('/update/my/info/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then(res => {
        setUser(res.data);
      })
      .catch(err => {
        console.error("Update failed", err);
        throw err;
      });
  };

  const totalTrees = plantedTrees.length;
  const co2Absorbed = plantedTrees.length * 45; // 45kg average per tree

  // Dynamic gamification logic
  let level = 1;
  if (totalTrees >= 200) level = 5;
  else if (totalTrees >= 100) level = 4;
  else if (totalTrees >= 25) level = 3;
  else if (totalTrees >= 10) level = 2;

  const achievements = [
    { id: 1, icon: "🌱", title: "First Tree", desc: "Plant your very first tree", unlocked: totalTrees >= 1, level: 1 },
    { id: 6, icon: "🌳", title: "10 Trees Club", desc: "Plant 10 trees total", unlocked: totalTrees >= 10, level: 2 },
    { id: 11, icon: "🏆", title: "Green Hero", desc: "Plant 25 trees total", unlocked: totalTrees >= 25, level: 3 },
    { id: 16, icon: "💯", title: "Century Club", desc: "Plant 100 trees total", unlocked: totalTrees >= 100, level: 4 },
    { id: 21, icon: "🌲", title: "Forest Founder", desc: "Plant 200 trees total", unlocked: totalTrees >= 200, level: 5 }
  ];

  const levelInfo = [
    { level: 1, icon: "fa-seedling", title: "Level 1: Beginner", subtitle: "Start your green journey" },
    { level: 2, icon: "fa-leaf", title: "Level 2: Grower", subtitle: "Expand your forest" },
    { level: 3, icon: "fa-shield-halved", title: "Level 3: Eco Warrior", subtitle: "Make a significant impact" },
    { level: 4, icon: "fa-crown", title: "Level 4: Forest Guardian", subtitle: "Become a leader" },
    { level: 5, icon: "fa-star", title: "Level 5: Legend", subtitle: "Ultimate achievements" }
  ];

  const badgesEarned = achievements.filter(a => a.unlocked).length;
  const progressPercent = Math.min(100, Math.round((badgesEarned / 5) * 100));

  const profileAvatar = User.profile_picture ? `http://127.0.0.1:8000${User.profile_picture}` : "/default_avatar.jpg";

  return (
    <div className="profile-container">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-left-col">
          <div className="profile-avatar-container">
            <img src={profileAvatar} alt="Profile" className="profile-avatar" />
          </div>
          <div className="left-stats-row">
            <div className="left-stat-box">
              <div className="left-stat-value">{totalTrees}</div>
              <div className="left-stat-label">{t('prof_trees')}</div>
            </div>
            <div className="left-stat-box">
              <div className="left-stat-value">{co2Absorbed}kg</div>
              <div className="left-stat-label">CO₂</div>
            </div>
            <div className="left-stat-box">
              <div className="left-stat-value">{badgesEarned}</div>
              <div className="left-stat-label">{t('prof_badges')}</div>
            </div>
          </div>
        </div>

        <div className="profile-right-col">
          <div className="profile-head-row">
            <h1 className="profile-name">{User.name}</h1>
            <button className="btn-edit-outline" onClick={handleEdit}>
              <i className="fa-solid fa-pen"></i> {t('prof_edit')}
            </button>
          </div>

          <div className="profile-job">
            <i className="fa-solid fa-code"></i> {User.user?.first_name} {User.user?.last_name}
          </div>

          <div className="bio-box">{User.bio || 'Please add a bio!'}</div>

          <div className="details-footer">
            <div className="detail-col">
              <div className="detail-head">{t('prof_location')}</div>
              <div className="detail-val">
                <i className="fa-solid fa-location-dot" style={{ color: 'var(--primary)' }}></i> {User.region}
              </div>
            </div>
            <div className="detail-col">
              <div className="detail-head">Email</div>
              <div className="detail-val">{User.user?.email || 'N/A'}</div>
            </div>
            <div className="detail-col">
              <div className="detail-head">Phone</div>
              <div className="detail-val">{User.phoneNumber}</div>
            </div>
            <div className="detail-col">
              <div className="detail-head">Joined</div>
              <div className="detail-val">{User.user?.date_joined?.split("T")[0]}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="content-grid">
        <div className="section-card full-height">
          <h2 className="section-title">
            <i className="fa-solid fa-tree"></i> {t('prof_my_trees') || 'My Planted Trees'} ({plantedTrees.length})
          </h2>

          <div className="tree-list">
            {plantedTrees.map(item => {
              const bkt = item.bucket || {};
              const tree = bkt.product?.tree || {};
              return (
                <div key={item.id} className="tree-item" style={{ borderLeft: '4px solid var(--primary)' }}>
                  <div className="tree-header">
                    <div>
                      <div className="tree-name">{getLocalized(tree, language, 'name') || 'Planted Tree'}</div>
                      <div className="tree-scientific">{tree.family?.name || ''}</div>
                    </div>
                    <div className="tree-id">ID: #{item.id}</div>
                  </div>
                  <div className="tree-info-grid">
                    <div className="tree-info-item">
                      <span className="info-icon"><i className="fa-solid fa-calendar"></i></span>
                      <div>
                        <div className="info-label">{t('prof_planted_on') || 'Planted On'}</div>
                        <div className="info-value">{item.plantingDate?.split("T")[0] || 'Unknown'}</div>
                      </div>
                    </div>
                    {bkt.count && (
                      <div className="tree-info-item">
                        <span className="info-icon"><i className="fa-solid fa-cubes"></i></span>
                        <div>
                          <div className="info-label">{t('orders_quantity') || 'Qty'}</div>
                          <div className="info-value">{bkt.count}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    className="btn-show-on-map"
                    onClick={() => navigate(`/${ROUTES.MAP}?tree=${item.id}`)}
                  >
                    <i className="fa-solid fa-map-location-dot"></i> {t('prof_view_map') || 'Show on Map'}
                  </button>
                </div>
              );
            })}

            {plantedTrees.length === 0 && <p style={{ margin: '20px', color: 'var(--text-light)' }}>No planted trees yet. Your ordered trees will appear here once planted!</p>}
          </div>
        </div>

        {/* Sidebar */}
        <div className="sidebar-container">
          <div className="section-card" style={{ minHeight: '200px' }}>
            <h2 className="section-title">
              <i className="fa-solid fa-clock-rotate-left"></i> {t('prof_recent_activity')}
            </h2>
            {activities.length === 0 && <p style={{ marginTop: '10px' }}>No recent activity.</p>}
            {activities.map(act => (
              <div key={act.id} className="activity-item">
                <div className="activity-icon">
                  <i className={`fa-solid ${act.action_type.includes('plant') ? 'fa-tree' : 'fa-box'}`}></i>
                </div>
                <div className="activity-content">
                  <div className="activity-text">{act.description}</div>
                  <div className="activity-time">{act.timestamp.split("T")[0]}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gamification Roadmap */}
      <div className="section-card" style={{ marginTop: '20px' }}>
        <h2 className="section-title">
          <i className="fa-solid fa-trophy"></i> {t('prof_roadmap')}
        </h2>

        <div className="achievement-progress">
          <div className="progress-header">
            <span className="progress-text">{t('prof_progress')}: {badgesEarned} of 5</span>
            <span className="progress-percentage">{progressPercent}%</span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }}></div>
          </div>
        </div>

        <div className="achievement-roadmap">
          {levelInfo.map(lv => (
            <div key={lv.level}>
              <div className="achievement-level">
                <div className="level-title">
                  <i className={`fa-solid ${lv.icon}`}></i> {lv.title} {level >= lv.level && '(Achieved)'}
                </div>
                <div className="level-subtitle">{lv.subtitle}</div>
              </div>
              {achievements.filter(a => a.level === lv.level).map(a => (
                <div key={a.id} className={`achievement-item ${a.unlocked ? 'unlocked' : 'locked'}`}>
                  <span className="achievement-status"><i className={`fa-solid ${a.unlocked ? 'fa-check' : 'fa-lock'}`}></i></span>
                  <span className="achievement-icon-large">{a.icon}</span>
                  <div className="achievement-title">{a.title}</div>
                  <div className="achievement-desc">{a.desc}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      <EditProfileModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        userData={User ? { ...User, user: User.user } : null}
        onSave={handleSaveProfile}
      />
    </div>
  );
};

export default Profile;